import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import EditIcon from "@mui/icons-material/Edit";
import {
  Grid,
  TextField,
  Box,
  Button,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Tooltip,
  FormControlLabel,
  IconButton,
  Stack,
  MenuItem,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";
import DeleteIcon from "@mui/icons-material/Delete";
import toast from "react-hot-toast";
import {
  retrieveAllReservationsApi,
  cancelReservationApi,
  updateReservationApi,
  retrieveFilteredReservedOrdersApi,
  retrieveReservationByIdApi,
} from "../../../components/api/ReservationApiService";
import { createPurchaseOrderFromReservationApi } from "../../../components/api/OrderApiService";
import ReservationDialog from "../VendorOfferView/Dialogs/ReservationDialog";
import { companies, leafForm, origin, recordStatus, type } from "../../../utils/menu-items";
import { retrieveAllOffersApi, retrieveOfferByIdApi } from "../../../components/api/OfferApiService";
import CreateOrder from "../VendorOfferView/CreateOrder";
import { retrieveAllItemsApi, retrieveFilteredItemsApi } from "../../../components/api/ItemApiService";

// Define headCells for reservation table columns
const headCells = [
  { id: 'actions', label: '', disablePadding: true },
  { id: 'id', label: 'ID', numeric: true },
  { id: 'offer.id', label: 'Offer ID', numeric: true },
  { id: 'offer.item.id', label: 'Item ID', numeric: true },
  { id: 'offer.item.buyingGrade', label: 'Buying Grade' },
  { id: 'offer.salesGrade', label: 'Sales Grade' },
  { id: 'offer.item.crop', label: 'Crop' },
  { id: 'offer.stakeholder.name', label: 'Stakeholder' },
  { id: 'company', label: 'Company' },
  { id: 'netWeightPerCase', label: 'Net Wt/Case', numeric: true },
  { id: 'numberOfCases', label: 'Cartons', numeric: true },
  { id: 'quantity', label: 'Quantity', numeric: true },
  { id: 'remainingQuantity', label: 'Remaining Quantity', numeric: true },
  { id: 'volumeUnit', label: 'Unit' },
  { id: 'remark', label: 'Remark' },
  { id: 'reservationDate', label: 'Reservation Date' },
  { id: 'validityDate', label: 'Validity Date' },
  { id: 'status', label: 'Status' },
  { id: 'recordStatus', label: 'Record Status' },
];

function getByPath(obj, path) {
  return path
    .split('.')
    .reduce((cur, key) => (cur == null ? undefined : cur[key]), obj);
}
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'asc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const cmp = comparator(a[0], b[0]);
    return cmp !== 0 ? cmp : a[1] - b[1];
  });
  return stabilized.map(el => el[0]);
}

function EnhancedTableHead(props) {
  const {
    numSelected,
    rowCount,
    onSelectAllClick,
    order,
    orderBy,
    onRequestSort
  } = props;
  const createSortHandler = property => event => onRequestSort(event, property);


  return (
    <TableHead sx={{ backgroundColor: '#20315d' }}>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'select all rows' }}
          />
        </TableCell>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'center' : 'center'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              whiteSpace: 'nowrap',
              color: '#fff',
            }}
          >
            {headCell.id === 'actions' ? (
              headCell.label
            ) : (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  rowCount: PropTypes.number.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  onRequestSort: PropTypes.func.isRequired,
};



function EnhancedTableToolbar({ numSelected, onDelete, showFilters, setShowFilters }) {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1">
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%', fontWeight: 600, fontSize: "20px" }}
          variant="h6"
          id="tableTitle"
          component="div"
          color="#1E2685"
        >
          PO Reservations
        </Typography>
      )}
      <Stack direction={"row"} spacing={1} sx={{ mr: 2 }}>
        <Button
          variant="outlined"
          onClick={() => setShowFilters(!showFilters)}
          sx={{ minWidth: "130px" }}
          size="small"
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
      </Stack>
    </Toolbar>
  );
}
EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onDelete: PropTypes.func,
};

export default function ReservedVendorOrderView() {
  const navigate = useNavigate();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [selected, setSelected] = useState([]);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dense, setDense] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [offers, setOffers] = useState([]);
  const [items, setItems] = useState([]);
  const [filterReservedOrder, setFilterReservedOrder] = useState({
    id: 0,
    tobaccoCode: "",
    salesGrade: "",
    buyingGrade: "",
    customerGrade: "",
    internalGrade: "",
    originList: [],
    typeList: [],
    leafFormList: [],
    colorList: [],
    itemIdList: [],
    offerIdList: [],
    statusList: ["ACTIVE"],
  });
  const [componentsState, setComponentsState] = useState({
    origin: {
      dropdownOpen: false,
      inputValue: "",
    },
    item: {
      dropdownOpen: false,
      inputValue: "",
    },
    vendor: {
      dropdownOpen: false,
      inputValue: "",
    },
    type: {
      dropdownOpen: false,
      inputValue: "",
    },
    leafForm: {
      dropdownOpen: false,
      inputValue: "",
    },
    storage: {
      dropdownOpen: false,
      inputValue: "",
    },
    cost: {
      dropdownOpen: false,
      inputValue: "",
    },
    firstSample: {
      dropdownOpen: false,
      inputValue: "",
    },
    secondSample: {
      dropdownOpen: false,
      inputValue: "",
    },
    paymentTerm: {
      dropdownOpen: false,
      inputValue: "",
    },
    offer: {
      dropdownOpen: false,
      inputValue: "",
    },
  });

  function retrieveOffers() {
    retrieveAllOffersApi("VENDOR")
      .then((response) => {
        setOffers(response?.data);
      })
      .catch((error) => console.log(error.response?.data));
  }

  function retrieveItems() {
    retrieveAllItemsApi()
      .then((response) => {
        setItems(response?.data);
      })
      .catch((error) => console.log(error.response?.data));
  }

  const updateComponentState = (componentKey, newState) => {
    setComponentsState((prevState) => ({
      ...prevState,
      [componentKey]: {
        ...prevState[componentKey],
        ...newState,
      },
    }));
  };

  const [editDialog, setEditDialog] = useState({ open: false, reservation: null });
  const [editData, setEditData] = useState({});

  const [showOrderPopup, setShowOrderPopup] = useState({
    visible: false,
    data: null,
  });

  useEffect(() => {
    retrieveAllReservationsApi('VENDOR')
      .then(res => setRows(res.data))
      .catch(err => console.error(err));
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = event => {
    if (event.target.checked) setSelected(rows.map(n => n.id));
    else setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) newSelected = selected.concat(id);
    else if (selectedIndex === 0) newSelected = selected.slice(1);
    else if (selectedIndex === selected.length - 1) newSelected = selected.slice(0, -1);
    else newSelected = [
      ...selected.slice(0, selectedIndex),
      ...selected.slice(selectedIndex + 1)
    ];
    setSelected(newSelected);
  };

  /*const handleDelete = () => {
    Promise.all(selected.map(id => cancelReservationApi([id])))
      .then(() => {
        toast.success('Cancelled successfully');
        setRows(rows.filter(r => !selected.includes(r.id)));
        setSelected([]);
      })
      .catch(() => toast.error('Cancellation failed'));
  };*/

  const cancelReservation = async (reservation) => {
    try {
      const response = await cancelReservationApi(reservation?.id);
      toast.success("Reservation cancelled successfully.");
    } catch (error) {
      console.log(error.response.data);
      toast.error("Something went wrong. ", error.response.data);
    }
  }
  
  const isSelected = id => selected.indexOf(id) !== -1;
  const emptyRows = page > 0
    ? Math.max(0, (1 + page) * rowsPerPage - rows.length)
    : 0;

  const visibleRows = useMemo(
    () => stableSort(rows, getComparator(order, orderBy))
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, order, orderBy, page, rowsPerPage]
  );

  return (
    <div>
      {showOrderPopup.visible ? (
        <CreateOrder
          rows={rows}
          setRows={setRows}
          setCreateOrder={setShowOrderPopup}
          createOrder={showOrderPopup}
        />
      ) : (
        <>
          {showFilters && (
            <Box
              component="form"
              sx={{
                "& .MuiTextField-root": { m: 1, width: "25ch" },
              }}
              noValidate
              autoComplete="off"
            >
              <Grid
                container
                spacing={1}
                justifyContent="flex-start"
                alignItems="center"
              >
                <Autocomplete
                  multiple
                  id="outlined-select-offer"
                  disableClearable
                  open={componentsState.offer.dropdownOpen}
                  onOpen={() =>
                    updateComponentState("offer", { dropdownOpen: true })
                  }
                  inputValue={componentsState.offer.inputValue}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      updateComponentState("offer", {
                        inputValue: newInputValue,
                      });
                    }
                  }}
                  options={[
                    ...offers.filter((offer) =>
                      filterReservedOrder.offerIdList.includes(offer.id),
                    ),
                    ...offers.filter(
                      (offer) =>
                        !filterReservedOrder.offerIdList.includes(offer.id),
                    ),
                  ]}
                  getOptionLabel={(option) =>
                    `ID: ${option?.id || ""} Offer Number: ${option?.offerYear || ""
                    } - ${option?.offerGroupNumber || ""}`
                  }
                  value={offers.filter((offer) =>
                    filterReservedOrder.offerIdList.includes(offer.id),
                  )}
                  onChange={(event, newValue) => {
                    setFilterReservedOrder({
                      ...filterReservedOrder,
                      offerIdList: newValue.map((offer) => offer.id),
                    });
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li
                      {...props}
                      style={{
                        ...props.style,
                        backgroundColor: selected ? "lightblue" : undefined,
                        fontWeight: selected ? 700 : 400,
                      }}
                    >
                      {`ID: ${option?.id || ""} Offer Number: ${option?.offerYear || ""
                        } - ${option?.offerGroupNumber || ""}`}
                    </li>
                  )}
                  renderInput={(params) => {
                    const label =
                      filterReservedOrder?.offerIdList.length > 0
                        ? `Offers Selected (${filterReservedOrder?.offerIdList.length})`
                        : "Offer";

                    return (
                      <TextField
                        {...params}
                        label={label}
                        type="search"
                        value={componentsState.offer.inputValue}
                        onChange={(e) =>
                          updateComponentState("offer", {
                            inputValue: e.target.value,
                          })
                        }
                        onMouseDown={retrieveOffers}
                        InputProps={{
                          ...params.InputProps,
                          style: { height: 42 },
                        }}
                        onBlur={() =>
                          updateComponentState("offer", { dropdownOpen: false })
                        }
                      />
                    );
                  }}
                  renderTags={() => null}
                />

                <Autocomplete
                  multiple
                  id="outlined-select-item"
                  disableClearable
                  open={componentsState.item.dropdownOpen}
                  onOpen={() =>
                    updateComponentState("item", { dropdownOpen: true })
                  }
                  inputValue={componentsState.item.inputValue}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      updateComponentState("item", {
                        inputValue: newInputValue,
                      });
                    }
                  }}
                  options={[{ id: null, buyingGrade: "-", crop: "" }, ...items]}
                  getOptionLabel={(option) =>
                    option.id !== null
                      ? `ID: ${option?.id || ""} - ${option?.buyingGrade || ""
                      } - ${option?.crop || ""}`
                      : "-"
                  }
                  value={items.filter((item) =>
                    filterReservedOrder.itemIdList.includes(item.id),
                  )}
                  onChange={(event, newValue) => {
                    setFilterReservedOrder({
                      ...filterReservedOrder,
                      itemIdList: newValue
                        .map((item) => item.id)
                        .filter((id) => id !== null),
                    });
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li
                      {...props}
                      style={{
                        ...props.style,
                        backgroundColor: selected ? "lightblue" : undefined,
                        fontWeight: selected ? 700 : 400,
                      }}
                    >
                      {option.id !== null
                        ? `ID: ${option?.id || ""} - ${option?.buyingGrade || ""
                        } - ${option?.crop || ""}`
                        : "-"}
                    </li>
                  )}
                  renderInput={(params) => {
                    const label =
                      filterReservedOrder?.itemIdList.length > 0
                        ? `Items Selected (${filterReservedOrder?.itemIdList.length})`
                        : "Item";

                    return (
                      <TextField
                        {...params}
                        label={label}
                        type="search"
                        value={componentsState.item.inputValue}
                        onChange={(e) =>
                          updateComponentState("item", {
                            inputValue: e.target.value,
                          })
                        }
                        onMouseDown={retrieveItems}
                        InputProps={{
                          ...params.InputProps,
                          style: { height: 42 },
                        }}
                        onBlur={() =>
                          updateComponentState("item", { dropdownOpen: false })
                        }
                      />
                    );
                  }}
                  renderTags={() => null}
                />

                <TextField
                  label="Status"
                  type="search"
                  id="outlined-select-status"
                  select
                  SelectProps={{
                    multiple: true,
                    value: filterReservedOrder.statusList || [],
                    onChange: (e) => {
                      setFilterReservedOrder({
                        ...filterReservedOrder,
                        statusList: e.target.value,
                      });
                    },
                  }}
                >
                  {recordStatus.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid
                container
                spacing={2}
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid item xs={3} sm={1}>
                  <Button
                    disableElevation
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      retrieveFilteredReservedOrdersApi(filterReservedOrder)
                        .then((response) => {
                          setRows(response?.data);
                        })
                        .catch((error) => {
                          console.log(error?.response?.data);
                        });
                    }}
                  >
                    Search
                  </Button>
                </Grid>
                <Grid item xs={3} sm={1}>
                  <Button
                    disableElevation
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setFilterReservedOrder({
                        id: 0,
                        tobaccoCode: "",
                        salesGrade: "",
                        buyingGrade: "",
                        customerGrade: "",
                        internalGrade: "",
                        originList: [],
                        typeList: [],
                        leafFormList: [],
                        colorList: [],
                        crop: null,
                        storageList: [],
                        nicotine: null,
                        itemIdList: [],
                        offerIdList: [],
                        statusList: ["ACTIVE"],
                      });
                    }}
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
          <Box sx={{ width: '100%' }}>
            <Paper sx={{ mb: 2 }}>
              <EnhancedTableToolbar numSelected={selected.length} /*onDelete={handleDelete}*/ showFilters={showFilters} setShowFilters={setShowFilters} />
              <TableContainer>
                <Table
                  size={dense ? 'small' : 'medium'}
                  sx={{ tableLayout: 'auto', width: '100%' }}
                >
                  <EnhancedTableHead
                    numSelected={selected.length}
                    rowCount={rows.length}
                    onSelectAllClick={handleSelectAllClick}
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody>
                    {visibleRows.map((row, index) => {
                      const isItemSelected = isSelected(row.id);
                      const labelId = `enhanced-table-checkbox-${index}`;
                      return (
                        <TableRow
                          style={
                            isSelected(row?.id)
                              ? {
                                background: "#d7e7fc",
                              }
                              : index % 2
                                ? { background: "#f0f0f0" }
                                : { background: "white" }
                          }
                          key={row?.id}
                          hover
                          role="checkbox"
                          aria-checked={isItemSelected}
                          selected={isItemSelected}
                          sx={{ height: 32 }}
                          onClick={event => handleClick(event, row.id)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isItemSelected}
                              onChange={event => handleClick(event, row.id)}
                              inputProps={{ 'aria-labelledby': labelId }}
                            />
                          </TableCell>
                          <TableCell padding="none" sx={{ whiteSpace: 'nowrap' }}>
                            <Button
                              sx={{ marginRight: '10px' }}
                              variant="outlined"
                              onClick={() => {
                                if (!row?.id) {
                                  alert("Please refresh the page.");
                                  return;
                                }
                                setEditDialog({ open: true, reservation: row });
                                setEditData({
                                  company: row?.company || "",
                                  reservationDate: row?.reservationDate || "",
                                  validityDate: row?.validityDate || "",
                                  numberOfCases: row?.numberOfCases || "",
                                  netWeightPerCase: row?.netWeightPerCase || "",
                                  quantity: row?.quantity || "",
                                  volumeUnit: row?.volumeUnit || "",
                                  remark: row?.remark || "",
                                  recordStatus: row?.recordStatus || "",
                                });
                              }}
                            >
                              Edit
                            </Button>

                            <Button
                              sx={{ marginRight: '10px' }}
                              variant="outlined"
                              disabled={
                                row?.recordStatus === "PASSIVE"
                              }
                              onClick={() => {
                                if (!row?.id) {
                                  alert("Please refresh the page.");
                                  return;
                                }
                                retrieveOfferByIdApi(row?.offer?.id)
                                  .then(({ data }) => {
                                    setShowOrderPopup({
                                      data: {
                                        ...data,
                                        volume: row?.quantity,
                                        numberOfCases: row?.numberOfCases,
                                        netWeightPerCase: row?.netWeightPerCase,
                                        reservationId: row?.id,
                                        operation: "Reserve"
                                      },
                                      visible: true,
                                    });
                                  })
                                  .catch((err) => {
                                    console.error("Failed to load offer:", err);
                                    toast.error("Couldn’t load offer details.");
                                  });
                              }
                              }
                            >
                              Create PO
                            </Button>
                            <Button
                              variant="outlined"
                              color="warning"
                              onClick={() => {
                                if (!row?.id) {
                                  alert("Please refresh the page.");
                                  return;
                                }
                                 if (row?.quantity !== row?.remainingQuantity) {
                                  alert("Please cancel the connected POs first.");
                                  return;
                                }
                                 if (
                                        window.confirm(
                                          `Are you sure you want to cancel reservation ${row?.id || ""
                                          } for item: ${row?.offer?.item?.buyingGrade
                                          }`,
                                        )
                                      ) {
                                       retrieveReservationByIdApi(row?.id)
                                        .then((response) => {
                                          cancelReservation(response?.data);
                                        })
                                        .catch((error) => {
                                          window.alert("Failed to retrieve reservation. Please try again later.");
                                        });
                                      }
                              }}
                            >
                              Cancel Reservation
                            </Button>
                          </TableCell>
                          <ReservationDialog
                            open={editDialog?.open}
                            reservation={editDialog.reservation}
                            initialData={editData}
                            companies={companies}
                            onClose={() => setEditDialog({ open: false, reservation: null })}
                            onSave={updatedData => {
                              updateReservationApi(editDialog?.reservation?.id, updatedData)
                                .then(({ data }) => {
                                  toast.success('Reservation updated');
                                  setRows(rows.map(r => r.id === data.id ? data : r));
                                  setEditDialog({ open: false, reservation: null });
                                })
                                .catch(() => toast.error('Update failed'));
                            }}
                          />

                          {headCells.slice(1).map(cell => (
                            <TableCell
                              key={cell.id}
                              align={cell.numeric ? 'center' : 'center'}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {getByPath(row, cell.id)}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 32 * emptyRows }}>
                        <TableCell colSpan={headCells.length} />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={e => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
              <FormControlLabel
                control={<Switch checked={dense} onChange={e => setDense(e.target.checked)} />}
                label="Dense padding"
              />
            </Paper>
          </Box>
        </>
      )}
    </div>
  );
}
